import type { JoinLanguage } from '../../services/talentJoinApi';

/**
 * The page's own words, in both languages. The CONSENT wording is not here: it comes from the
 * proxy (`lib/talentPool/config.js`), so legal reviews one file and the page cannot drift from it.
 */
export const JOIN_COPY = {
  en: {
    title: 'Join the FINN Talent Community',
    preview: 'Preview for review — not open to candidates yet. Only @finn.com addresses can sign up.',
    draft: 'Consent wording',
    name: 'Name',
    firstName: 'First name',
    lastName: 'Last name',
    email: 'Email',
    linkedin: 'LinkedIn profile (optional)',
    cluster: 'Which area interests you most?',
    submit: 'Sign up',
    sending: 'Sending…',
    sentTitle: 'Check your inbox',
    sentBody: 'We have sent you a link to confirm your sign-up. It only counts once you click it.',
    errors: {
      required: 'Please fill this in.',
      invalid: 'This does not look right.',
      jobMatches: 'Please tick this box to join.',
      not_open: 'This page is not open yet.',
      failed: 'Something went wrong. Please try again in a moment.',
      network: 'We could not reach FINN. Please check your connection and try again.',
    },
    confirmTitle: 'Confirm your sign-up',
    confirmBody: 'One click and you are in the FINN Talent Community.',
    confirmButton: 'Confirm my sign-up',
    confirmedTitle: 'You are in',
    confirmedBody: 'Thanks for joining. We will get in touch when a role opens that fits you.',
    manageLink: 'See or change your details',
    bookmark: 'We have also emailed you this link, so you can come back to it any time.',
    invalidLink: 'This link is no longer valid. It may have been used already, or it has expired.',
    manageTitle: 'Your FINN Talent Community details',
    whatWeHold: 'What we hold about you',
    consentUntil: 'Your consent is valid until',
    updatesLabel: 'Send me occasional news from the FINN team',
    deleteTitle: 'Leave the Talent Community',
    deleteBody: 'This deletes your details straight away. You can join again any time.',
    deleteButton: 'Delete my data',
    deleteConfirm: 'Yes, delete everything',
    cancel: 'Cancel',
    deletedTitle: 'Your data is deleted',
    deletedBody: 'Nothing about you is kept in the FINN Talent Community. Thanks for your interest in FINN.',
    loading: 'Loading…',
  },
  de: {
    title: 'Werde Teil der FINN Talent Community',
    preview: 'Vorschau zur Prüfung — noch nicht für Kandidat:innen geöffnet. Nur @finn.com-Adressen können sich anmelden.',
    draft: 'Einwilligungstext',
    name: 'Name',
    firstName: 'Vorname',
    lastName: 'Nachname',
    email: 'E-Mail',
    linkedin: 'LinkedIn-Profil (optional)',
    cluster: 'Welcher Bereich interessiert dich am meisten?',
    submit: 'Anmelden',
    sending: 'Wird gesendet…',
    sentTitle: 'Schau in dein Postfach',
    sentBody: 'Wir haben dir einen Link zur Bestätigung geschickt. Deine Anmeldung gilt erst, wenn du ihn anklickst.',
    errors: {
      required: 'Bitte ausfüllen.',
      invalid: 'Das sieht nicht richtig aus.',
      jobMatches: 'Bitte setze diesen Haken, um dabei zu sein.',
      not_open: 'Diese Seite ist noch nicht geöffnet.',
      failed: 'Etwas ist schiefgelaufen. Bitte versuch es gleich noch einmal.',
      network: 'FINN ist gerade nicht erreichbar. Bitte prüfe deine Verbindung und versuch es erneut.',
    },
    confirmTitle: 'Anmeldung bestätigen',
    confirmBody: 'Ein Klick, und du bist Teil der FINN Talent Community.',
    confirmButton: 'Anmeldung bestätigen',
    confirmedTitle: 'Du bist dabei',
    confirmedBody: 'Danke! Wir melden uns, sobald eine passende Stelle frei wird.',
    manageLink: 'Deine Daten ansehen oder ändern',
    bookmark: 'Diesen Link haben wir dir auch per E-Mail geschickt, damit du jederzeit zurückkommen kannst.',
    invalidLink: 'Dieser Link ist nicht mehr gültig. Er wurde schon benutzt oder ist abgelaufen.',
    manageTitle: 'Deine Daten in der FINN Talent Community',
    whatWeHold: 'Was wir über dich gespeichert haben',
    consentUntil: 'Deine Einwilligung gilt bis',
    updatesLabel: 'Schickt mir gelegentlich Neuigkeiten aus dem FINN Team',
    deleteTitle: 'Talent Community verlassen',
    deleteBody: 'Damit löschen wir deine Daten sofort. Du kannst jederzeit wieder beitreten.',
    deleteButton: 'Meine Daten löschen',
    deleteConfirm: 'Ja, alles löschen',
    cancel: 'Abbrechen',
    deletedTitle: 'Deine Daten sind gelöscht',
    deletedBody: 'In der FINN Talent Community ist nichts mehr über dich gespeichert. Danke für dein Interesse an FINN.',
    loading: 'Wird geladen…',
  },
} as const;

export type JoinCopy = (typeof JOIN_COPY)[JoinLanguage];

/**
 * Language: the link's own `?lang=` wins, else the browser's language. The toggle on the page is
 * only an override for the exception.
 */
export function initialLanguage(search: string, navigatorLanguage: string | undefined): JoinLanguage {
  const fromLink = new URLSearchParams(search).get('lang');
  if (fromLink === 'de' || fromLink === 'en') return fromLink;
  return `${navigatorLanguage || ''}`.toLowerCase().startsWith('de') ? 'de' : 'en';
}
